import { useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, useLocation } from "react-router";
import { styled } from "@pigment-css/react";
import { EmptyState, EmptyTitle } from "@acto/taskpane/field-sales-2/components/primitives/EmptyState";
import { useTranslations } from "@acto/web-common/locales";
import { BulletList, CheckMarkSquare, Info, MessageCheck, PlusIcon, TodosIcon } from "@acto/web-common/components";
import type { Todo } from "@acto/taskpane/core/domain";
import { AnalyticsEvents, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { TopBar } from "@acto/taskpane/field-sales-2/components/patterns/TopBar";
import type { FilterPopoverGroup } from "@acto/taskpane/field-sales-2/components/patterns/FilterPopover";
import { TodoBucketSection } from "@acto/taskpane/field-sales-2/components/patterns/TodoBucketSection";
import { PageLoadingSkeleton } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout/PageLoadingSkeleton";
import { TodoUndoToasts } from "@acto/taskpane/field-sales-2/pages/TodoUndoToasts";
import { useHomeNavActionPill } from "@acto/taskpane/field-sales-2/HomeNavLayout";
import { BottomNavActionPill } from "@acto/taskpane/field-sales-2/components/patterns/BottomNav/BottomNavActionPill";
import { CreateTodoOverlay } from "@acto/taskpane/field-sales-2/components/patterns/CreateTodoOverlay";
import { TodoEditSheet } from "@acto/taskpane/field-sales-2/components/patterns/TodoEditSheet";
import { useToast, TOAST_TRANSITION_DELAY } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import {
  useAllTodosForPage,
  type TodoBucketFilter,
} from "@acto/taskpane/field-sales-2/data-access/todos/useAllTodosForPage";
import { useOptimisticTodoCompletion } from "@acto/taskpane/field-sales-2/pages/useOptimisticTodoCompletion";
import { useOptimisticTodoReassignment } from "@acto/taskpane/field-sales-2/pages/useOptimisticTodoReassignment";
import { useUpdateTodos } from "@acto/taskpane/core/data-access/todos/useUpdateTodos";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { useContributeChatScope, todosToBuckets } from "@acto/taskpane/core/chat-envelope";
import { TODO_LIST_LIMIT } from "@acto/taskpane/field-sales-2/utils/chatScope";

const Page = styled("main")({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  // Reserve clearance so the last row scrolls clear of the fixed bottom nav, plus extra breathing
  // room so the "+ Neue Aufgabe" pill never overlaps the final card.
  paddingBottom: "calc(var(--bottom-nav-opaque-height, 0px) + 24px)",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[48],
  paddingTop: theme.fieldSalesTwo.spacing[24],
}));

type FilterKey = TodoBucketFilter;

export function AllTodos() {
  const t = useTranslations("fieldSalesTwo.todosList");
  const tRoot = useTranslations("fieldSalesTwo");
  const tNav = useTranslations("fieldSalesTwo.bottomNav");
  const tCreate = useTranslations("fieldSalesTwo.todoCreate");
  const navigate = useDirectionalNavigate();
  const location = useLocation();

  const { displayValue, debouncedValue, setDisplayValue } = useDebouncedSearchTerm();
  // Deep-link support: a pre-set filter can arrive via navigation state (e.g. the Home
  // "Überfällige Aufgaben" pill routes here with `initialFilter: "overdue"`; the bottom-nav tab
  // navigates with no state). Seeded lazily so the first render already has the right filter —
  // avoids firing the disjoint open/completed bucket queries and an empty-chip flash. The user
  // can switch filters freely afterwards.
  const initialFilter = (location.state as { initialFilter?: FilterKey } | null)?.initialFilter;
  const [filterKey, setFilterKey] = useState<FilterKey>(() =>
    initialFilter === "all" || initialFilter === "overdue" || initialFilter === "open" || initialFilter === "completed"
      ? initialFilter
      : "all",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const { showToast } = useToast();

  useHomeNavActionPill(
    useMemo(
      () => (
        <BottomNavActionPill onClick={() => setCreateOpen(true)}>
          <PlusIcon aria-hidden="true" />
          {tNav("newTodo")}
        </BottomNavActionPill>
      ),
      [tNav],
    ),
  );

  const { overdue, open, completed, isPending, error, refetch } = useAllTodosForPage({
    searchTerm: debouncedValue,
    filter: filterKey,
  });
  const { updateTodos } = useUpdateTodos();
  const track = useTrack();

  // Refresh on (re-)navigation: the page remounts on route change and `usePagination` serves
  // cache-first, so force a network refetch on mount — edits/reassignments made elsewhere (recap,
  // the customer tab, another user) then appear without a manual reload. Safe against the optimistic
  // overlays: navigating away unmounts the page, which flushes any pending grace-period batch first.
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useViewTrackedOnce(() =>
    track(AnalyticsEvents.TODOS_VIEWED, { source: initialFilter === "overdue" ? "overdue_banner" : "todos_overview" }),
  );

  const {
    overdueShown,
    openShown,
    completedShown,
    isCompleting,
    handleComplete,
    handleUndo,
    pendingCount,
    isReverting,
    handleUncomplete,
    handleUndoRevert,
    revertPendingCount,
    flushPending: flushCompletionBatches,
  } = useOptimisticTodoCompletion(updateTodos, {
    overdue: overdue.todos,
    open: open.todos,
    completed: completed.todos,
  });

  const shownTodos = useMemo(
    () => [...overdueShown, ...openShown, ...completedShown],
    [overdueShown, openShown, completedShown],
  );
  const {
    assigneeNameFor,
    handleReassign,
    handleUndo: handleReassignUndo,
    pendingCount: reassignPendingCount,
  } = useOptimisticTodoReassignment(updateTodos, shownTodos, flushCompletionBatches);

  const openCustomer = useCallback(
    (todo: Todo) => {
      if (!todo.client) return;
      navigate(generatePath(ActoRoutes.CUSTOMER_DETAILS, { id: encodeURIComponent(todo.client.id) }), {
        state: { from: location.pathname },
      });
    },
    [navigate, location.pathname],
  );

  const showOverdue = filterKey === "all" || filterKey === "overdue";
  const showOpen = filterKey === "all" || filterKey === "open";
  const showCompleted = filterKey === "all" || filterKey === "completed";

  // Ambient context: contribute the search/filter state and only the buckets it keeps visible.
  const todosFragment = useMemo(
    () => ({
      search_term: debouncedValue || undefined,
      filter: filterKey,
      ...todosToBuckets(
        {
          overdue: showOverdue ? overdueShown : undefined,
          open: showOpen ? openShown : undefined,
          completed: showCompleted ? completedShown : undefined,
        },
        { limit: TODO_LIST_LIMIT },
      ),
    }),
    [debouncedValue, filterKey, showOverdue, overdueShown, showOpen, openShown, showCompleted, completedShown],
  );
  useContributeChatScope("todos", todosFragment);

  const visibleCount =
    (showOverdue ? overdueShown.length : 0) +
    (showOpen ? openShown.length : 0) +
    (showCompleted ? completedShown.length : 0);
  // Base loading/empty on the buckets actually visible for the current filter, so switching filters
  // doesn't flash the empty state off stale data from a now-hidden bucket.
  const isLoading = isPending && visibleCount === 0;
  const allEmpty = !isLoading && !error && visibleCount === 0;

  const filterLabels: Record<FilterKey, string> = {
    all: t("filterAll"),
    overdue: t("sections.overdue"),
    open: t("sections.open"),
    completed: t("sections.completed"),
  };

  // Groups mirror the Figma separators: [Alle] · [überfällig, Offen] · [Abgeschlossen].
  const filterGroups: FilterPopoverGroup[] = useMemo(
    () => [
      { key: "all", items: [{ key: "all", label: filterLabels.all, icon: <BulletList /> }] },
      {
        key: "active",
        items: [
          { key: "overdue", label: filterLabels.overdue, icon: <Info /> },
          { key: "open", label: filterLabels.open, icon: <TodosIcon /> },
        ],
      },
      { key: "done", items: [{ key: "completed", label: filterLabels.completed, icon: <CheckMarkSquare /> }] },
    ],
    [filterLabels.all, filterLabels.overdue, filterLabels.open, filterLabels.completed],
  );

  return (
    <Page>
      <TopBar
        title={t("title")}
        search={{ value: displayValue, onChange: setDisplayValue }}
        filter={{
          groups: filterGroups,
          selectedKeys: [filterKey],
          onToggle: (key) => setFilterKey(key as FilterKey),
          activeSummary: filterKey === "all" ? undefined : { firstLabel: filterLabels[filterKey], extraCount: 0 },
        }}
      />
      {error ? (
        <EmptyState>
          <EmptyTitle>{tRoot("error", { defaultValue: "Something went wrong" })}</EmptyTitle>
        </EmptyState>
      ) : isLoading ? (
        <PageLoadingSkeleton />
      ) : allEmpty ? (
        <EmptyState>
          <TodosIcon aria-hidden="true" />
          <EmptyTitle>{t("empty")}</EmptyTitle>
        </EmptyState>
      ) : (
        <ContentWrapper>
          {showOverdue && (overdueShown.length > 0 || overdue.hasNextPage) && (
            <TodoBucketSection
              title={t("sections.overdue")}
              todos={overdueShown}
              variant="overdue"
              hasNextPage={overdue.hasNextPage}
              isFetching={overdue.isFetching}
              onLoadMore={overdue.fetchNextPage}
              loadMoreLabel={t("loadMore")}
              isCompleting={isCompleting}
              onComplete={handleComplete}
              onUncomplete={handleUncomplete}
              isReverting={isReverting}
              onCustomerClick={openCustomer}
              assigneeNameFor={assigneeNameFor}
              onRowClick={setEditingTodo}
            />
          )}
          {showOpen && (openShown.length > 0 || open.hasNextPage) && (
            <TodoBucketSection
              title={t("sections.open")}
              todos={openShown}
              variant="open"
              hasNextPage={open.hasNextPage}
              isFetching={open.isFetching}
              onLoadMore={open.fetchNextPage}
              loadMoreLabel={t("loadMore")}
              isCompleting={isCompleting}
              onComplete={handleComplete}
              onUncomplete={handleUncomplete}
              isReverting={isReverting}
              onCustomerClick={openCustomer}
              assigneeNameFor={assigneeNameFor}
              onRowClick={setEditingTodo}
            />
          )}
          {showCompleted && (completedShown.length > 0 || completed.hasNextPage) && (
            <TodoBucketSection
              title={t("sections.completed")}
              todos={completedShown}
              variant="completed"
              hasNextPage={completed.hasNextPage}
              isFetching={completed.isFetching}
              onLoadMore={completed.fetchNextPage}
              loadMoreLabel={t("loadMore")}
              isCompleting={isCompleting}
              onComplete={handleComplete}
              onUncomplete={handleUncomplete}
              isReverting={isReverting}
              onCustomerClick={openCustomer}
              assigneeNameFor={assigneeNameFor}
            />
          )}
        </ContentWrapper>
      )}
      <TodoUndoToasts
        completionPendingCount={pendingCount}
        onUndoCompletion={handleUndo}
        revertPendingCount={revertPendingCount}
        onUndoRevert={handleUndoRevert}
        reassignPendingCount={reassignPendingCount}
        onUndoReassignment={handleReassignUndo}
      />
      <TodoEditSheet
        todo={editingTodo}
        isOpen={editingTodo !== null}
        onOpenChange={(open) => {
          if (!open) setEditingTodo(null);
        }}
        onReassign={handleReassign}
        onSaved={refetch}
      />
      <CreateTodoOverlay
        isOpen={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          showToast({
            message: tCreate("successToast"),
            icon: <MessageCheck aria-hidden="true" />,
            initialDelay: TOAST_TRANSITION_DELAY,
          });
          refetch();
        }}
      />
    </Page>
  );
}
